import React, { useEffect, useMemo, useState } from "react"

import { eachWeekOfInterval, endOfWeek, format, isAfter, startOfDay, subDays } from "date-fns"
import { JwtPayload, jwtDecode } from "jwt-decode"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuth } from "@/contexts/AuthContext"
import {
  type FinancialImpactFilter,
  fetchAllTickets,
  useTicketNameOptions,
  useTickets,
  useTicketsCount,
} from "@/hooks/useTickets"
import { exportTicketsToCSV } from "@/utils/csvExportUtils"

import TicketsFilters from "../tickets/TicketsFilters"
import TicketsTable from "../tickets/TicketsTable"

interface TicketsJwtClaims extends JwtPayload {
  sub_teams?: unknown
}

const TicketsContent: React.FC = () => {
  // Default to the last 30 days (inclusive of today)
  const defaultStartDate = useMemo(() => subDays(new Date(), 30), [])
  const defaultEndDate = useMemo(() => new Date(), [])
  const { session } = useAuth()

  const [startDate, setStartDate] = useState<Date>(defaultStartDate)
  const [endDate, setEndDate] = useState<Date>(defaultEndDate)
  const [searchQuery, setSearchQuery] = useState("")
  const [financialImpactFilter, setFinancialImpactFilter] = useState<FinancialImpactFilter>("all")
  const [statusFilter, setStatusFilter] = useState("")
  const [ticketTypeIdFilter, setTicketTypeIdFilter] = useState<number | null>(null)
  const [driverManagerTeamFilter, setDriverManagerTeamFilter] = useState("")
  const [weekPage, setWeekPage] = useState(
    () =>
      eachWeekOfInterval(
        { start: startOfDay(defaultStartDate), end: startOfDay(defaultEndDate) },
        { weekStartsOn: 1 },
      ).length || 1,
  )
  const [isExporting, setIsExporting] = useState(false)

  // Driver manager team options come from JWT sub_teams
  const driverManagerTeamOptions = useMemo(() => {
    const token = session?.access_token
    if (!token) return []

    try {
      const claims = jwtDecode<TicketsJwtClaims>(token)
      const rawSubTeams = Array.isArray(claims.sub_teams) ? claims.sub_teams : []

      return Array.from(
        new Set(
          rawSubTeams
            .filter((value): value is string => typeof value === "string")
            .map((value) => value.trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b))
    } catch (error) {
      console.error("Failed to decode tickets sub_teams from JWT", error)
      return []
    }
  }, [session?.access_token])

  const periodStartDate = useMemo(() => startOfDay(startDate), [startDate])
  const periodEndDate = useMemo(() => startOfDay(endDate), [endDate])
  const hasValidDateRange = !isAfter(periodStartDate, periodEndDate)

  const weekStarts = useMemo(() => {
    if (!hasValidDateRange) return []

    return eachWeekOfInterval({ start: periodStartDate, end: periodEndDate }, { weekStartsOn: 1 })
  }, [hasValidDateRange, periodEndDate, periodStartDate])

  const totalWeeks = weekStarts.length
  const currentWeekPage = totalWeeks === 0 ? 0 : Math.min(Math.max(weekPage, 1), totalWeeks)
  const activeWeekStart = currentWeekPage > 0 ? weekStarts[currentWeekPage - 1] : undefined
  const activeWeekEnd = activeWeekStart
    ? endOfWeek(activeWeekStart, { weekStartsOn: 1 })
    : undefined

  const activeQueryStart =
    activeWeekStart && activeWeekStart < periodStartDate ? periodStartDate : activeWeekStart
  const activeQueryEnd =
    activeWeekEnd && activeWeekEnd > periodEndDate ? periodEndDate : activeWeekEnd

  const periodStartFormatted = format(periodStartDate, "yyyy-MM-dd")
  const periodEndFormatted = format(periodEndDate, "yyyy-MM-dd")
  const activeWeekFrom = activeQueryStart
    ? `${format(activeQueryStart, "yyyy-MM-dd")}T00:00:00`
    : undefined
  const activeWeekTo = activeQueryEnd
    ? `${format(activeQueryEnd, "yyyy-MM-dd")}T23:59:59.999`
    : undefined
  const periodDateFrom = hasValidDateRange ? `${periodStartFormatted}T00:00:00` : undefined
  const periodDateTo = hasValidDateRange ? `${periodEndFormatted}T23:59:59.999` : undefined

  const {
    data,
    isLoading: isWeekLoading,
    error: weekError,
  } = useTickets({
    dateFrom: activeWeekFrom,
    dateTo: activeWeekTo,
    searchQuery,
    financialImpactFilter,
    statusFilter: statusFilter === "all" ? "" : statusFilter,
    ticketTypeIdFilter,
    driverManagerTeamFilter: driverManagerTeamFilter === "all" ? "" : driverManagerTeamFilter,
  })
  const {
    data: totalCount = 0,
    isLoading: isTotalCountLoading,
    error: countError,
  } = useTicketsCount({
    dateFrom: periodDateFrom,
    dateTo: periodDateTo,
    searchQuery,
    financialImpactFilter,
    statusFilter: statusFilter === "all" ? "" : statusFilter,
    ticketTypeIdFilter,
    driverManagerTeamFilter: driverManagerTeamFilter === "all" ? "" : driverManagerTeamFilter,
  })
  const { data: ticketTypeOptions = [] } = useTicketNameOptions()

  const isLoading = isWeekLoading || isTotalCountLoading
  const error = weekError ?? countError
  const weekTickets = data?.tickets || []
  const totalMatchingTickets = totalCount
  const activeWeekLabel =
    activeWeekStart && activeWeekEnd
      ? `${format(activeWeekStart, "dd MMM yyyy")} - ${format(activeWeekEnd, "dd MMM yyyy")}`
      : ""

  useEffect(() => {
    setWeekPage(totalWeeks > 0 ? totalWeeks : 1)
  }, [
    totalWeeks,
    searchQuery,
    financialImpactFilter,
    statusFilter,
    ticketTypeIdFilter,
    driverManagerTeamFilter,
  ])

  const handleExportCSV = async () => {
    if (totalMatchingTickets === 0) {
      toast.error("No tickets to export")
      return
    }

    setIsExporting(true)
    try {
      // Fetch all tickets for export with search applied
      // RLS handles access control at the database level
      const allTicketsForExport = await fetchAllTickets({
        dateFrom: periodDateFrom,
        dateTo: periodDateTo,
        searchQuery,
        financialImpactFilter,
        statusFilter: statusFilter === "all" ? "" : statusFilter,
        ticketTypeIdFilter,
        driverManagerTeamFilter: driverManagerTeamFilter === "all" ? "" : driverManagerTeamFilter,
      })

      const timestamp = format(new Date(), "yyyyMMdd_HHmmss")
      exportTicketsToCSV(
        allTicketsForExport,
        `tickets_${periodStartFormatted}_${periodEndFormatted}_${timestamp}.csv`,
      )
      toast.success(`Exported ${allTicketsForExport.length} tickets to CSV`)
    } catch (err) {
      console.error("Export error:", err)
      toast.error("Failed to export tickets")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Tickets</h2>
          <p className="text-palette-jungleGreen600">Search and export ticket information</p>
        </div>
      </div>

      {/* Filters */}
      <TicketsFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        financialImpactFilter={financialImpactFilter}
        onFinancialImpactFilterChange={setFinancialImpactFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        ticketTypeIdFilter={ticketTypeIdFilter}
        onTicketTypeIdFilterChange={setTicketTypeIdFilter}
        ticketTypeOptions={ticketTypeOptions}
        driverManagerTeamFilter={driverManagerTeamFilter}
        onDriverManagerTeamFilterChange={setDriverManagerTeamFilter}
        driverManagerTeamOptions={driverManagerTeamOptions}
        visibleCount={!error ? weekTickets.length : 0}
        totalCount={!error ? totalMatchingTickets : 0}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onExport={handleExportCSV}
        isExporting={isExporting}
        disableExport={totalMatchingTickets === 0}
      />

      {/* Results */}
      <div className="bg-white rounded-lg shadow-sm">
        {!hasValidDateRange ? (
          <div className="p-8 text-center text-palette-jungleGreen500">
            Select a valid date range to view tickets.
          </div>
        ) : isLoading ? (
          <div className="p-4 space-y-4">
            <div className="border rounded-lg overflow-hidden">
              {/* Header skeleton */}
              <div className="bg-palette-jungleGreen000 border-b">
                <div className="grid grid-cols-16 gap-4 p-3 min-w-[1400px]">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <Skeleton key={`tickets-head-skel-${i}`} className="h-4 w-full" />
                  ))}
                </div>
              </div>

              {/* Rows skeleton */}
              {Array.from({ length: 8 }).map((_, row) => (
                <div key={`tickets-row-skel-${row}`} className="border-b last:border-b-0">
                  <div className="grid grid-cols-16 gap-4 p-3 min-w-[1400px]">
                    {Array.from({ length: 16 }).map((_, col) => (
                      <Skeleton key={`tickets-cell-skel-${row}-${col}`} className="h-4 w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            Error loading tickets: {error instanceof Error ? error.message : "Unknown error"}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div className="text-sm font-medium text-palette-jungleGreen900">
                  Week {currentWeekPage} of {totalWeeks}
                </div>
                <div className="text-sm text-palette-jungleGreen600">
                  {activeWeekLabel}
                  {activeWeekLabel ? " • " : ""}
                  {weekTickets.length.toLocaleString()} ticket{weekTickets.length === 1 ? "" : "s"}{" "}
                  this week
                  {" • "}
                  {totalMatchingTickets.toLocaleString()} in selected period
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setWeekPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentWeekPage <= 1 || isLoading}
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Previous Week
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setWeekPage((prev) => Math.min(totalWeeks, prev + 1))}
                  disabled={currentWeekPage >= totalWeeks || isLoading}
                >
                  Next Week
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {activeWeekStart && activeWeekEnd ? (
              <TicketsTable
                tickets={weekTickets}
                weekStart={activeWeekStart}
                weekEnd={activeWeekEnd}
                selectedPeriodStart={periodStartDate}
                selectedPeriodEnd={periodEndDate}
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default TicketsContent
